/** Recover a viewport reset, never a legitimate scroll or content clamp.
 * One instance belongs to one conversation. No timers or repeated pins: a
 * correction requires two measured observations of the same scroll element.
 */
export function createChatScrollRecovery({ now = Date.now } = {}) {
  let previous = null;
  let pausedUntil = 0;
  let interacting = false;

  const pause = () => {
    previous = null;
    pausedUntil = now() + 1500;
  };
  const observe = (element, { blocked = false } = {}) => {
    if (!element?.isConnected) { previous = null; return false; }
    const current = {
      element,
      offset: element.scrollTop,
      height: element.scrollHeight,
      viewport: element.clientHeight,
    };
    if (![current.offset, current.height, current.viewport].every(Number.isFinite)
      || current.viewport <= 0) { previous = null; return false; }

    const last = previous;
    previous = current;
    if (blocked || interacting || now() < pausedUntil || last?.element !== element) return false;
    // Growing a reply must leave the reader where they were. Shrinking a
    // thinking block, resizing, or removing content can legitimately clamp
    // scrollTop, so those changes deliberately establish a new baseline.
    if (last.offset <= 80 || current.offset > 2 || current.offset < 0
      || current.height < last.height
      || Math.abs(current.viewport - last.viewport) > 1
      || current.height - current.viewport < last.offset) return false;

    element.scrollTop = last.offset;
    previous = { ...current, offset: element.scrollTop };
    return previous.offset > 2;
  };

  const listen = (container) => {
    const win = container?.ownerDocument?.defaultView;
    if (!win) return () => {};
    const begin = () => { interacting = true; pause(); };
    const end = () => { if (interacting) { interacting = false; pause(); } };
    const blur = (event) => {
      // Capture also sees input/button blur when focus moves within the page.
      if (event.target !== win) return;
      interacting = false;
      pause();
    };
    const key = (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Tab'].includes(event.key)) pause();
    };
    const options = { passive: true, capture: true };
    const subscriptions = [
      [container, 'wheel', pause],
      [container, 'pointerdown', begin],
      [container, 'touchstart', begin],
      [container, 'touchmove', pause],
      [container, 'focusin', pause],
      [win, 'pointerup', end],
      [win, 'pointercancel', end],
      [win, 'touchend', end],
      [win, 'touchcancel', end],
      [win, 'blur', blur],
      [win, 'keydown', key],
    ];
    subscriptions.forEach(([target, type, handler]) => target.addEventListener(type, handler, options));
    return () => {
      subscriptions.forEach(([target, type, handler]) => target.removeEventListener(type, handler, options));
      interacting = false;
      previous = null;
    };
  };
  return { observe, pause, listen };
}
